import React, {useState} from 'react';
import {ConsultationSummary} from '@/interface';
import {useFormik} from 'formik';
import {TextInputClinicalNote} from './TextInputClinicalNote';
import {ArrayInputClinicalNote} from './ArrayInputClinicalNote';
import {Button, Icon, Text, TextInput} from '@/ui-kits';
import {MedicationsClinicalNote} from './MedicationsClinicalNote';
import {SpeechToTextRecorder} from './SpeechToTextRecorder';
import { alphaNumericWithPeriodRegex } from '@/constants';
import { formatName } from '@/utils/common_functions';
import {useSelector} from 'react-redux';
import {transcribeAudioLoadingStatus} from '@/services/redux/selectors/specialistDashboardSelector';

interface ClinicalNoteEditableFormProps {
  initialValues: ConsultationSummary['summary'];
  isReadonly: boolean;
  onSubmit: (values: ConsultationSummary['summary']) => void;
  onRecommendedNextStepsSave?: () => void;
}
export const ClinicalNoteEditableForm: React.FC<ClinicalNoteEditableFormProps> = props => {
  const {initialValues, isReadonly, onSubmit, onRecommendedNextStepsSave} = props;
  const {values, handleChange, setFieldValue, handleSubmit, dirty} = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true,
  });

  const [isEditing, setIsEditing] = useState<Record<keyof ConsultationSummary['summary'], boolean>>({
    chief_complaint: false,
    history_summary: false,
    diagnoses: false,
    medication: false,
    plan: false,
    recommended_next_steps: false,
  });

  const [hasNewTranscription, setHasNewTranscription] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [hasManualChanges, setHasManualChanges] = useState(false);
  const transcribeLoading = useSelector(transcribeAudioLoadingStatus);

  const handleToggleEdit = (field: keyof ConsultationSummary['summary']) => {
    if (isReadonly) return;
    setIsEditing(prev => ({...prev, [field]: !prev[field]}));
  };

  const handleSaveChanges = () => {
    handleSubmit();
    setIsEditing(prev => {
      const newState = {...prev} as typeof prev;
      let key: keyof ConsultationSummary['summary'];
      for (key in newState) {
        newState[key] = false;
      }
      return newState;
    });
  };

  return (
    <div className="flex flex-col bg-ui_bg_pale_blue border border-ui_input_border_dark/60 rounded-lg p-4 sm:p-6 gap-4 sm:gap-6">
      {/* Clinical Note Heading */}
      <div className="flex flex-col gap-1 mb-2">
        <Text variant="heading" className="text-ui_blue !text-xl sm:!text-2xl font-FigtreeBold">
          Clinical Note
        </Text>
        <Text className="font-FigtreeRegular !text-xs sm:!text-sm text-ui_input_border_dark">This information will be shared with the patient.</Text>
      </div>

      <TextInputClinicalNote
        isEditing={isEditing.chief_complaint}
        onToggleEdit={() => handleToggleEdit('chief_complaint')}
        index={1}
        label="Chief Complaint"
        value={formatName(values.chief_complaint, false)}
        onChange={handleChange('chief_complaint')}
        placeholder="Enter Chief Complaint"
        multiline
        isReadonly={isReadonly}
      />
      <TextInputClinicalNote
        isEditing={isEditing.history_summary}
        onToggleEdit={() => handleToggleEdit('history_summary')}
        index={2}
        label="History Summary"
        value={formatName(values.history_summary, false)}
        onChange={handleChange('history_summary')}
        placeholder="Enter History Summary"
        multiline
        isReadonly={isReadonly}
      />
      <ArrayInputClinicalNote
        isEditing={isEditing.diagnoses}
        onToggleEdit={() => handleToggleEdit('diagnoses')}
        index={3}
        label={values.diagnoses.length > 1 ? 'Diagnoses' : 'Diagnosis'}
        values={values.diagnoses}
        onChange={values => setFieldValue('diagnoses', values)}
        placeholder="Add New Diagnoses"
        isReadonly={isReadonly}
      />
      <MedicationsClinicalNote
        index={4}
        medications={values.medication}
        onChange={medications => setFieldValue('medication', medications)}
        isReadonly={isReadonly}
        isGlobalEditing={isEditing.medication}
        onSetIsEditing={() => setIsEditing(prev => ({...prev, medication: true}))}
      />
      <TextInputClinicalNote
        isEditing={isEditing.plan}
        onToggleEdit={() => handleToggleEdit('plan')}
        index={5}
        label="Plan / Next Steps"
        value={values.plan}
        onChange={handleChange('plan')}
        placeholder="Medical Plan for the patient"
        multiline
        isReadonly={isReadonly}
      />

      <div className="bg-white p-4 rounded-md border flex-col flex gap-3 border-ui_input_border_dark">
        <div className="flex gap-2 items-center">
          <Icon type="recommended_steps" />
          <Text variant="subtitle" className="text-ui_blue !text-base sm:!text-lg">
            Recommended next steps
          </Text>
        </div>

        <div className="relative">
          <TextInput
            value={values.recommended_next_steps}
            onChangeText={(text) => {
              handleChange('recommended_next_steps')(text);
              setHasManualChanges(true);
            }}
            placeholder={transcribeLoading ? 'Transcribing...' : 'Enter Recommended next steps'}
            multiline
            max={1000}
            preventLeadingSpace
            preventTrailingSpace
            allowedRegex={alphaNumericWithPeriodRegex}
            wrapperClassName={`border ${isRecording ? 'pb-10' : ''}`}
            disabled={isReadonly || transcribeLoading || isRecording}
          />

          {!isReadonly && (
            <div className={`absolute bottom-2 flex items-center gap-2 bg-white px-1 rounded-md ${isRecording ? 'left-2 right-2' : 'right-2'}`}>
              <SpeechToTextRecorder
                onTranscriptionComplete={(text) => {
                  const currentText = values.recommended_next_steps?.trim();
                  const newText = currentText ? `${currentText}\n${text}` : text;
                  setFieldValue('recommended_next_steps', newText);
                  setHasNewTranscription(true);
                }}
                onRecordingStateChange={setIsRecording}
                disabled={transcribeLoading}
              />
              {(hasNewTranscription || hasManualChanges) && !isRecording && (
                <Button
                  title="Save"
                  variant="primary"
                  size="sm"
                  disabled={transcribeLoading}
                  onClick={() => {
                    setHasNewTranscription(false);
                    setHasManualChanges(false);
                    onRecommendedNextStepsSave?.();
                    handleSubmit();
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
